import { useCallback, useEffect, useRef, useState } from 'react';
import { pickRecorderMimeType } from './videoApi';
import { inNativeApp } from './nativeApp';
import useNativeLiveRecording from './useNativeLiveRecording';

// Records the whole assessment and streams it to the assessment while it runs:
// a piece every few seconds goes to PUT /api/profiles/:id/videos/live/:uploadId,
// so long sessions have no size limit and a closed tab loses at most a few
// seconds (the server finishes abandoned recordings by itself).
//
//   const live = useLiveRecording();
//   live.start({ profileId, title, facing })  → camera on, recording
//   live.pause() / live.resume()
//   live.finish()   → stops, uploads the rest, saves the video to the assessment
//   live.discard()  → stops and deletes the recording
//   live.status: idle | starting | recording | paused | finishing | saved | error
//   live.stream (for a preview <video>), live.elapsed (s), live.backlog (pieces waiting), live.error

const PIECE_MS = 4000;
const base = (profileId) =>
  `/api/profiles/${encodeURIComponent(profileId)}/videos/live`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function cameraError(err, en) {
  const name = err && err.name;
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return en
      ? 'Camera access is blocked — allow it in the browser settings.'
      : 'Доступ к камере запрещён — разрешите его в настройках браузера.';
  if (name === 'NotFoundError')
    return en
      ? 'No camera found on this device.'
      : 'На этом устройстве нет камеры.';
  if (name === 'NotReadableError')
    return en
      ? 'The camera is busy in another app.'
      : 'Камера занята другим приложением.';
  return (
    (err && err.message) ||
    (en ? 'Could not start the camera.' : 'Не удалось включить камеру.')
  );
}

function useBrowserLiveRecording({ en = false } = {}) {
  const [status, setStatusState] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [backlog, setBacklog] = useState(0);
  const [error, setError] = useState('');
  const [stream, setStream] = useState(null);
  const [saved, setSaved] = useState(null);
  const r = useRef({});
  const statusRef = useRef('idle');
  const setStatus = (s) => {
    statusRef.current = s;
    setStatusState(s);
  };

  const recordedMs = () => {
    const x = r.current;
    if (!x.startedAt) return 0;
    const now = x.pauseStart || Date.now();
    return now - x.startedAt - (x.pausedMs || 0);
  };

  const stopTimer = () => {
    clearInterval(r.current.timer);
    r.current.timer = null;
  };

  const stopCamera = () => {
    const x = r.current;
    if (x.stream) x.stream.getTracks().forEach((t) => t.stop());
    x.stream = null;
    setStream(null);
  };

  // Send waiting pieces in order; retry with backoff while the network is down
  const pump = useCallback(async () => {
    const x = r.current;
    if (x.sending || !x.uploadId) return;
    x.sending = true;
    let wait = 1000;
    while (x.queue && x.queue.length && !x.dropped) {
      const piece = x.queue[0];
      try {
        const res = await fetch(
          `${base(x.profileId)}/${x.uploadId}?seq=${piece.seq}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: piece.blob,
          }
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          x.queue.shift();
          wait = 1000;
        } else if (
          res.status === 409 &&
          typeof data.nextSeq === 'number' &&
          data.nextSeq > piece.seq
        ) {
          x.queue = x.queue.filter((p) => p.seq >= data.nextSeq); // already stored
        } else if (res.status === 409 || res.status === 404) {
          x.dropped = true; // recording was finished or removed elsewhere
          break;
        } else {
          throw new Error(data.message || `HTTP ${res.status}`);
        }
      } catch (e) {
        await sleep(wait);
        wait = Math.min(wait * 2, 15000);
      }
      setBacklog(x.queue.length);
    }
    setBacklog(x.queue ? x.queue.length : 0);
    x.sending = false;
  }, []);

  const start = useCallback(
    async ({ profileId, title = '', facing = 'user' }) => {
      if (!['idle', 'saved', 'error'].includes(statusRef.current) || !profileId)
        return false;
      setError('');
      setSaved(null);
      setStatus('starting');
      const x = { profileId, queue: [], seq: 0, pausedMs: 0 };
      r.current = x;
      try {
        x.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: true,
        });
        if (r.current !== x) {
          x.stream.getTracks().forEach((t) => t.stop());
          return false;
        }
        setStream(x.stream);
        const mimeType = pickRecorderMimeType();
        const res = await fetch(base(profileId), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mimeType: (mimeType || 'video/webm').split(';')[0],
            title,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        x.uploadId = data.uploadId;
        const rec = new MediaRecorder(x.stream, {
          ...(mimeType ? { mimeType } : {}),
          videoBitsPerSecond: 600000,
          audioBitsPerSecond: 64000,
        });
        x.recorder = rec;
        rec.ondataavailable = (e) => {
          if (!e.data || !e.data.size) return;
          x.queue.push({ seq: x.seq++, blob: e.data });
          setBacklog(x.queue.length);
          pump();
        };
        x.stopped = new Promise((resolve) => {
          rec.onstop = resolve;
        });
        rec.start(PIECE_MS);
        x.startedAt = Date.now();
        x.timer = setInterval(
          () => setElapsed(Math.round(recordedMs() / 1000)),
          500
        );
        setElapsed(0);
        setStatus('recording');
        return true;
      } catch (err) {
        stopCamera();
        stopTimer();
        if (x.uploadId)
          fetch(`${base(profileId)}/${x.uploadId}`, {
            method: 'DELETE',
            credentials: 'include',
          }).catch(() => {});
        r.current = {};
        setError(cameraError(err, en));
        setStatus('error');
        return false;
      }
    },
    [en, pump]
  );

  const pause = useCallback(() => {
    const x = r.current;
    if (
      statusRef.current !== 'recording' ||
      !x.recorder ||
      typeof x.recorder.pause !== 'function'
    )
      return;
    x.recorder.pause();
    x.pauseStart = Date.now();
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    const x = r.current;
    if (statusRef.current !== 'paused' || !x.recorder) return;
    x.recorder.resume();
    x.pausedMs += Date.now() - x.pauseStart;
    x.pauseStart = null;
    setStatus('recording');
  }, []);

  const stopRecorder = async () => {
    const x = r.current;
    if (!x.recorder) return;
    if (x.pauseStart) {
      x.pausedMs += Date.now() - x.pauseStart;
      x.pauseStart = null;
    }
    if (x.recorder.state !== 'inactive') {
      try {
        x.recorder.stop();
      } catch (e) {
        /* already stopped */
      }
    }
    await Promise.race([x.stopped, sleep(3000)]);
    stopTimer();
    stopCamera();
  };

  // Stop and save to the assessment. Resolves with the saved video (or null).
  const finish = useCallback(
    async ({ waitMs = 20000 } = {}) => {
      const x = r.current;
      if (x.finishing) return x.finishing;
      if (!x.recorder || !['recording', 'paused'].includes(statusRef.current))
        return null;
      setStatus('finishing');
      x.finishing = (async () => {
        const durationSec = Math.round(recordedMs() / 1000);
        await stopRecorder();
        pump();
        const until = Date.now() + waitMs;
        while (
          (x.queue.length || x.sending) &&
          Date.now() < until &&
          !x.dropped
        )
          await sleep(250);
        try {
          const res = await fetch(`${base(x.profileId)}/${x.uploadId}/finish`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ durationSec }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
          x.dropped = true;
          setSaved(data.asset || null);
          if (x.queue.length) {
            setError(
              en
                ? 'The last seconds could not be uploaded.'
                : 'Последние секунды записи не загрузились.'
            );
          }
          setStatus('saved');
          return data.asset || null;
        } catch (e) {
          setError(
            en
              ? `Video not saved: ${e.message}`
              : `Видео не сохранено: ${e.message}`
          );
          setStatus('error');
          return null;
        }
      })();
      return x.finishing;
    },
    [en, pump]
  );

  const discard = useCallback(async () => {
    const x = r.current;
    if (!x.recorder && !x.uploadId) return;
    x.dropped = true;
    await stopRecorder();
    x.queue = [];
    setBacklog(0);
    if (x.uploadId) {
      await fetch(`${base(x.profileId)}/${x.uploadId}`, {
        method: 'DELETE',
        credentials: 'include',
      }).catch(() => {});
    }
    r.current = {};
    setStatus('idle');
    setElapsed(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the page mid-recording: save what was recorded
  useEffect(
    () => () => {
      const x = r.current;
      if (x.recorder && ['recording', 'paused'].includes(statusRef.current)) {
        try {
          x.recorder.stop();
        } catch (e) {
          /* ignore */
        }
        if (x.stream) x.stream.getTracks().forEach((t) => t.stop());
        clearInterval(x.timer);
        fetch(`${base(x.profileId)}/${x.uploadId}/finish`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            durationSec: Math.round(recordedMs() / 1000),
          }),
          keepalive: true,
        }).catch(() => {});
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const active = ['starting', 'recording', 'paused', 'finishing'].includes(
    status
  );
  return {
    status,
    active,
    elapsed,
    backlog,
    error,
    stream,
    saved,
    start,
    pause,
    resume,
    finish,
    discard,
  };
}

// Inside the Android app the app records instead of the browser: the screen
// with the teacher's face in a corner and the microphone (useNativeLiveRecording).
const useLiveRecording = inNativeApp()
  ? useNativeLiveRecording
  : useBrowserLiveRecording;
export default useLiveRecording;
